import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';

import {
  adminOrdersMetaSelector,
  adminOrdersSelector,
  getAdminOrders,
} from 'src/store/adminOrders';
import { adminCurrenciesSelector, getAdminCurrencies } from 'src/store/adminCurrency';
import { getStatuses, statusesSelector } from 'src/store/exchanges';

import { REACT_APP_API_URL, ROUTE } from 'src/constants';
import { ProfileInfoIcon } from 'src/assets';

export const useBuild = () => {
  const { t } = useTranslation('adminBuilder');

  const dispatch = useDispatch();

  const adminOrders = useSelector(adminOrdersSelector);
  const adminOrdersMeta = useSelector(adminOrdersMetaSelector);
  const currencies = useSelector(adminCurrenciesSelector);
  const statuses = useSelector(statusesSelector);

  const [searchParams, setSearchParams] = useSearchParams();

  const [filtersData, setFiltersData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(getAdminCurrencies());
    dispatch(getStatuses());
  }, [dispatch]);

  useEffect(() => {
    const convertedParams = Object.fromEntries(searchParams.entries());

    setFiltersData(convertedParams);
    setCurrentPage(Number(convertedParams?.page) || 1);

    dispatch(getAdminOrders(convertedParams));
  }, [dispatch, searchParams]);

  const onSubmitFilters = useCallback(
    (filters) => {
      setCurrentPage(1);

      dispatch(getAdminOrders({ ...filters }));
    },
    [dispatch],
  );

  const onChangePage = useCallback(
    (page) => {
      setCurrentPage(page);

      const convertedParams = Object.fromEntries(searchParams.entries());

      const data = { ...convertedParams, ...filtersData, page: page?.toString() };

      for (const key in data) {
        if (!data[key]) {
          delete data[key];
        }
      }

      setSearchParams(data);
      dispatch(getAdminOrders(data));
    },
    [searchParams, filtersData, setSearchParams, dispatch],
  );

  const updatedCurrencies =
    currencies?.map((item) => ({ value: item?.id?.toString(), label: item?.name })) || [];

  const updatedStatuses =
    statuses?.map((item) => ({ value: item, label: t(`statuses:${item}`) })) || [];

  const build = {
    title: t('adminApplications:main'),
    styled_by_status: true,
    table_data: adminOrders,
    table_meta: { ...adminOrdersMeta, current_page: currentPage, on_change_page: onChangePage },
    filters_data: filtersData,
    is_visible_add: false,
    set_filters_data: setFiltersData,
    on_submit_filters: onSubmitFilters,
    main_route: ROUTE.ADMIN.ORDERS.MAIN,
    table_columns: [
      {
        id: 5,
        key: 'id',
        label: t('label:id'),
      },
      {
        id: 1,
        key: 'status',
        label: t('label:status'),
        value: ({ token, status }) => {
          return (
            <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'break-spaces' }}>
              <Link
                style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}
                to={`/exchange/${token}/status`}
                target="_blank"
              >
                <img src={ProfileInfoIcon} alt={ProfileInfoIcon} />
              </Link>
              {t(`statuses:${status}`)}
            </div>
          );
        },
      },
      {
        id: 2,
        key: 'created_at',
        label: t('label:created'),
        value: (item) => {
          return moment(item.created_at).format('DD.MM.YYYY, HH:mm');
        },
      },
      {
        id: 3,
        key: 'give',
        label: t('label:give'),
        value: ({ given }) => {
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'break-spaces' }}>
                <img
                  src={REACT_APP_API_URL + given?.image}
                  alt={REACT_APP_API_URL + given?.image}
                  style={{ width: 18, height: 18, marginRight: 10 }}
                />
                {given?.sum}{' '}
                <strong>
                  {given?.name} ({given?.code})
                </strong>
              </div>
              <div>{given?.cardNumber ? <span>({given?.code})</span> : null}</div>
            </div>
          );
        },
      },
      {
        id: 4,
        key: 'receive',
        label: t('label:receive'),
        value: ({ received }) => {
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'break-spaces' }}>
                <img
                  src={REACT_APP_API_URL + received?.image}
                  alt={REACT_APP_API_URL + received?.image}
                  style={{ width: 18, height: 18, marginRight: 10 }}
                />
                {received?.sum}{' '}
                <strong>
                  {received?.name} ({received?.code})
                </strong>
              </div>
              <div>{received?.cardNumber ? <span>{received?.cardNumber}</span> : null}</div>
            </div>
          );
        },
      },
    ],
    filters: [
      {
        key: 'id',
        label: t('searchById'),
        type: 'input',
      },
      {
        key: 'login',
        label: t('searchByLogin'),
        type: 'input',
      },
      {
        key: 'email',
        label: t('searchByEmail'),
        type: 'input',
      },
      {
        key: 'given',
        label: t('givingCurrency'),
        type: 'select',
        options: updatedCurrencies,
      },
      {
        key: 'received',
        label: t('receivingCurrency'),
        type: 'select',
        options: updatedCurrencies,
      },
      {
        key: 'status',
        label: t('status'),
        type: 'select',
        options: updatedStatuses,
      },
      {
        key: 'method',
        label: t('createdMethod'),
        type: 'select',
        options: [
          { value: 'website', label: t('label:website') },
          { value: 'telegram', label: t('label:telegram') },
        ],
      },
      {
        key: 'createdDate',
        label: t('createdDate'),
        type: 'datepicker',
      },
    ],
  };

  return { build };
};
